import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Home from "@/pages/Home";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ReportBug from "@/pages/ReportBug";
import PlateGenerator from "@/pages/PlateGenerator";
import Regulations from "@/pages/Regulations";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import NotFound from "@/pages/NotFound";
import ServerError from "@/pages/ServerError";
import ErrorBoundary from "@/components/ErrorBoundary";
import MaintenanceGuard from "@/components/MaintenanceGuard";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import PushPrompt from "@/components/PushPrompt";
import PwaUpdateBanner from "@/components/PwaUpdateBanner";
import CookieConsent from "@/components/CookieConsent";
import { registerServiceWorker } from "@/lib/registerSW";
import { Toaster } from "@/components/ui/sonner";

/** Renders the push-prompt on public routes only (never on /admin). */
function PublicOnlyPushPrompt() {
    const { pathname } = useLocation();
    if (pathname.startsWith("/admin")) return null;
    return <PushPrompt />;
}

function App() {
    useEffect(() => {
        registerServiceWorker();
    }, []);

    // Hide the boot splash (rendered in public/index.html) once the app has
    // committed its first paint. We wait for fonts + a minimum on-screen
    // duration so users don't see a flash of unstyled content and the
    // splash never disappears in a jarring sub-100ms flicker.
    useEffect(() => {
        const MIN_VISIBLE_MS = 500; // splash stays on-screen for at least this long
        const mountedAt = Date.now();

        const hideSplash = () => {
            const el =
                typeof document !== "undefined" &&
                document.getElementById("app-splash");
            if (!el || el.classList.contains("app-splash--hidden")) return;
            el.classList.add("app-splash--hidden");
            // Match the CSS fade duration (0.45 s) before removing from DOM.
            window.setTimeout(() => {
                if (el && el.parentNode) el.parentNode.removeChild(el);
            }, 600);
        };

        const scheduleHide = () => {
            const elapsed = Date.now() - mountedAt;
            const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
            // Hide directly (no requestIdleCallback) so the final phase doesn't
            // get delayed by up to ~800ms while the browser is busy mounting.
            window.setTimeout(hideSplash, wait);
        };

        // Wait for fonts + critical resources so the splash doesn't unmount
        // right before a FOUT/FOIT jump - but never wait longer than ~1.2s so
        // a slow/blocked webfont (e.g. an ORB-blocked Google Font) can't keep
        // the boot splash on screen. Whichever settles first wins.
        const fontsReady =
            typeof document !== "undefined" &&
            document.fonts &&
            typeof document.fonts.ready?.then === "function"
                ? document.fonts.ready
                : Promise.resolve();
        const maxWait = new Promise((resolve) => {
            window.setTimeout(resolve, 1200);
        });
        Promise.race([fontsReady, maxWait])
            .then(scheduleHide)
            .catch(scheduleHide);
    }, []);

    return (
        <HelmetProvider>
            <BrowserRouter>
                <ErrorBoundary>
                    <ThemeProvider>
                        <AuthProvider>
                        <MaintenanceGuard>
                            <AnnouncementBanner />
                            <Routes>
                                {/* Public routes (Polish only) */}
                                <Route path="/" element={<Home />} />
                                <Route path="/poi/:poiId" element={<Home />} />
                                <Route path="/zglos-blad" element={<ReportBug />} />
                                <Route path="/generator-tablic" element={<PlateGenerator />} />
                                <Route path="/regulamin" element={<Regulations />} />
                                <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
                                {/* Server error preview (used by admin TEST 500 link) */}
                                <Route path="/500" element={<ServerError />} />
                                {/* Admin */}
                                <Route path="/admin/login" element={<AdminLogin />} />
                                <Route path="/admin" element={<AdminDashboard />} />
                                {/* 404 */}
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                            <PublicOnlyPushPrompt />
                            <PwaUpdateBanner />
                            <CookieConsent />
                        </MaintenanceGuard>
                        <Toaster />
                    </AuthProvider>
                    </ThemeProvider>
                </ErrorBoundary>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
